
<template>
    <section class="help">
        <header class="help__header">
            <h1>Функционирование RTB</h1>
            <p class="help__lead">Подробное объяснение порядка проверок и фильтров, через которые проходит запрос на показ в нашем RTB-аукционе.</p>
        </header>

        <section class="help__section">
            <h2 class="help__section-title">Краткий обзор</h2>
            <p class="help__section-desc">
                Когда приходит RTB-запрос (SSP → наш DSP), система выполняет серию проверок сверху вниз. Если кандидат не проходит одну из проверок — он отбрасывается с понятной причиной.
            </p>
        </section>

        <section class="help__section">
            <h2 class="help__section-title">Порядок фильтров (сверху вниз)</h2>
            <ol class="help__steps">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Аутентификация и валидация запроса</h3>
                        <div class="step__content">
                            <p>
                                Проверяем заголовок <code>token</code> и входные параметры (timestampMs, duration, minPrice, auctionType).
                            </p>
                            <p><b>Ошибка</b>: <code>authentication</code></p>
                            <p><b>Текст ошибки</b>: «Аутентификация не пройдена»</p>
                            <div class="note">Код: <code>ImpressionRequestController::requestImpression()</code></div>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">2</div>
                    <div class="step__body">
                        <h3 class="step__title">Поиск экрана (Frame)</h3>
                        <div class="step__content">
                            <p>Ищем фрейм по GID и OID. Если экран не найден — запрос отклоняется.</p>
                            <p><b>Ошибка</b>: <code>no_frame</code></p>
                            <p><b>Текст ошибки</b>: «Экран не найден»</p>
                            <div class="note">Код: <code>ImpressionRequestController::findRecommendation()</code></div>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">3</div>
                    <div class="step__body">
                        <h3 class="step__title">Привязки (campaign_creative_frame)</h3>
                        <div class="step__content">
                            <p>Получаем записи привязок для данного экрана. Если их нет — запрос отклоняется.</p>
                            <p><b>Ошибка</b>: <code>no_campaign</code></p>
                            <p><b>Текст ошибки</b>: «Нет кампаний, привязанных к этому экрану»</p>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">4</div>
                    <div class="step__body">
                        <h3 class="step__title">Проверки креатива</h3>
                        <div class="step__content">
                            <p>Проверяем базовые атрибуты креатива.</p>
                            <p><b>Ошибка</b>: <code>no_creative_status</code></p>
                            <p><b>Текст ошибки</b>: «Нет креатива со статусом \"confirmed\"»</p>
                            <p><b>Ошибка</b>: <code>no_creative_url</code></p>
                            <p><b>Текст ошибки</b>: «Нет креатива с ссылкой (URL)»</p>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">5</div>
                    <div class="step__body">
                        <h3 class="step__title">Проверки кампании (раздельно)</h3>
                        <div class="step__content">
                            <ul>
                                <li><b>Статус</b> — код: <code>campaign_status</code></li>
                                <li><b>Дата старта</b> — код: <code>campaign_start_date</code></li>
                                <li><b>Дата окончания</b> — код: <code>campaign_end_date</code></li>
                                <li><b>Расписание</b> — код: <code>campaign_schedule</code></li>
                                <li><b>Бюджет итоговый</b> — код: <code>campaign_budget_total</code></li>
                                <li><b>Бюджет дневной</b> — код: <code>campaign_budget_daily</code></li>
                                <li><b>Показы итоговые</b> — код: <code>campaign_impr_total</code></li>
                                <li><b>Показы дневные</b> — код: <code>campaign_impr_daily</code></li>
                            </ul>
                            <div class="note">Код: <code>getCampaignRejection()</code> и методы модели <code>CampaignRtb</code></div>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">6</div>
                    <div class="step__body">
                        <h3 class="step__title">Проверки per-creative-per-frame (раздельно)</h3>
                        <div class="step__content">
                            <ul>
                                <li><b>Расписание</b> — код: <code>ccf_schedule</code></li>
                                <li><b>Бюджет итоговый</b> — код: <code>ccf_budget_total</code></li>
                                <li><b>Бюджет дневной</b> — код: <code>ccf_budget_daily</code></li>
                                <li><b>Показы итоговые</b> — код: <code>ccf_impr_total</code></li>
                                <li><b>Показы дневные</b> — код: <code>ccf_impr_daily</code></li>
                            </ul>
                            <div class="note">Код: <code>getCcfRejection()</code></div>
                        </div>
                    </div>
                </li>

                <!-- Targeting intentionally omitted per product decision -->

                <li class="step">
                    <div class="step__num">8</div>
                    <div class="step__body">
                        <h3 class="step__title">Технические требования</h3>
                        <div class="step__content">
                            <p>Привязки строго по размеру: если размеры креатива больше размера экрана — отклонение.</p>
                            <p><b>Пример ошибки</b>: «размер {creative.width}x{creative.height} != экран {frame.width}x{frame.height}»</p>
                            <div class="note">Код: <code>getTechnicalRejection()</code></div>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">9</div>
                    <div class="step__body">
                        <h3 class="step__title">Частотные проверки (recency / caps)</h3>
                        <div class="step__content">
                            <p>
                                Проверки выполняет ServingService: явные запреты (max_per_minute = 0 или min_interval_ms = 0),
                                вычисление применимого cap для текущего часа, перевод в интервалы (ms) и сравнение с таблицей rtb_last_shown.
                            </p>
                            <ul>
                                <li>Может вернуть причины: <code>schedule_blocked</code>, <code>cap_zero</code>, <code>interval_not_elapsed</code>.</li>
                                <li>Fail-open: при ошибках проверки частоты запросы обычно пропускаются (allow) и логируются.</li>
                            </ul>
                            <div class="note">Код: <code>App\Services\Rtb\ServingService::canServe()</code> и <code>describeCapNow()</code></div>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">10</div>
                    <div class="step__body">
                        <h3 class="step__title">Биддер и выбор ставки</h3>
                        <div class="step__content">
                            <p>
                                В SSP уходит <strong>один</strong> кандидат. Свои креативы и кампании на экране не соревнуются
                                ценой: очередь идёт по числу реальных побед. Если кандидат проиграл внешнему DSP — он остаётся
                                на слоте и AUTO поднимает рыночную ставку экрана (+STEP), пока не выиграет (или не упрётся в кап
                                package / бюджет). После победы очередь переходит к креативу с наименьшим числом показов.
                            </p>
                            <p>
                                <b>AUTO</b>: ставка считается в <code>ScreenMarketService</code> на ключе ssp+gid+duration
                                (не per-креатив). Старт = vendorMin, проигрыш SSP → +STEP, победа → клиринг и −STEP.
                                Кап package и остаток бюджета ограничивают ставку.
                            </p>
                            <p>
                                <b>MANUAL</b>: в SSP уходит ровно <code>CCF.bid_price</code> (конвертация в base через
                                <code>bidToBase</code>). Без step-up. Если ставка ниже minPrice — кандидат не участвует.
                                Проигрыш manual не держит sticky: очередь идёт дальше.
                            </p>
                            <p>Далее ставка AUTO/MANUAL дополнительно ограничивается остатками бюджета (capBidToRemainingBudget).</p>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">10b</div>
                    <div class="step__body">
                        <h3 class="step__title">Списание бюджета и метрики ролей</h3>
                        <div class="step__content">
                            <p>
                                После победы импрессия списывается с кампании по «начисленной» сумме (charged):
                                для manual это <code>bid_price</code>, преобразованный из валюты автора в base
                                (через <code>bidToBase</code> по текущим наценкам), для auto — реальный клиринг от SSP.
                                Параллельно ведётся отдельный счётчик <code>budget_total_fact_real</code> =
                                sum(<code>winner_price</code>) — то, что админ реально уплатил SSP. Разница между
                                charged и real — профит платформы (идёт админу).
                            </p>
                            <p>
                                Снапшоты <code>winner_price_agency</code> / <code>winner_price_viewer</code> в impression_requests:
                                для manual считаются через <code>convertBidBetweenRoles(bid_price, bid_role, target_role)</code> —
                                автор ставки видит ровно свой ввод, остальные роли получают значение от него по текущей цепочке наценок.
                                Для auto — от <code>winner_price × roleMultiplier</code>.
                            </p>
                            <p>
                                Просмотр статистики: agency и client видят свою сумму через наценку от charged.
                                Admin видит ДВЕ цифры — «Кампания израсходовала» (charged) и «Реально уплачено SSP» (real),
                                плюс профит. Енфорсмент лимита бюджета идёт по charged во всех режимах.
                            </p>
                            <p>
                                Каждый показ принимает решение по <strong>текущим</strong> наценкам и настройкам кампании
                                (включая campaign-level overrides из <code>entity_markups</code>). Снапшоты фризятся на момент
                                показа и не пересчитываются задним числом, поэтому исторические записи всегда отражают условия
                                «как было на тот момент».
                            </p>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">11</div>
                    <div class="step__body">
                        <h3 class="step__title">Победитель и ответ SSP</h3>
                        <div class="step__content">
                            <p>
                                Кандидат выбирается ротацией по победам (не max bid). Формируется ответ: mediaId, requestId, bid, auctionType, nurl.
                                Покупка аудиторных данных не меняет ставку: наценка 10% только в клиентском счёте.
                            </p>
                            <div class="note">Код: формирование ответа в <code>ImpressionRequestController::requestImpression()</code></div>
                        </div>
                    </div>
                </li>

                <li class="step">
                    <div class="step__num">12</div>
                    <div class="step__body">
                        <h3 class="step__title">Обработка колбэков (nurl / track)</h3>
                        <div class="step__content">
                            <p>
                                При успешном показе — SSP вызывает nurl/track, мы обновляем статус запроса, сохраняем screenshot (при наличии),
                                и инкрементируем счётчики (campaign_rtbs, campaign_creative_frame). Возможные ошибки логируются и не должны приводить к повторным списаниям.
                            </p>
                            <div class="note">Код: <code>ImpressionRequestController::track()</code>, <code>notifySuccess()</code>, <code>incrementSpendingCounters()</code></div>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section class="help__faq">
            <h2>Диагностика — быстрые советы</h2>
            <details>
                <summary>Получаю 401 при RTB запросе</summary>
                <p>Проверьте наличие и правильность заголовка <code>token</code>. См. resolveExpectedToken() в ImpressionRequestController.</p>
            </details>
            <details>
                <summary>Нету рекомендаций (204)</summary>
                <p>Откройте логи: возможные причины — отсутствуют привязки к экрану, частотные лимиты, проблемы с бюджетом, несоответствие таргетинга.</p>
            </details>
            <details>
                <summary>Частотные отказы</summary>
                <p>Проверьте таблицу <code>rtb_frequency_caps</code>, описания капа через <code>ServingService::describeCapNow()</code> и записи в <code>rtb_last_shown</code>.</p>
            </details>
        </section>

        <section class="help__section">
            <h2 class="help__section-title">Полезные ссылки в коде</h2>
            <ul>
                <li><code>app/Http/Controllers/ImpressionRequestController.php</code></li>
                <li><code>app/Services/Rtb/ServingService.php</code></li>
                <li><code>app/Services/Rtb/FrequencyHelper.php</code></li>
                <li><code>app/Services/Rtb/RtbFrequencyRepository.php</code> (репозиторий частот)</li>
            </ul>
        </section>
    </section>
</template>

<script>
export default {
    name: 'HelpRtb',
};
</script>

<style scoped>
/* Reuse styles from existing help page; scoped adjustments if needed */
.help__lead {
    color: #6b7280;
    margin-bottom: 12px;
}
.help__steps .step {
    margin: 12px 0;
}
.note {
    margin-top:8px;
    font-size:13px;
    color:#6b7280;
}
</style>

